import { useFormContext } from 'react-hook-form';
import { useCreatives } from '../api';
import type { CreativeEntry } from 'types/shared';
import { queryClient } from 'lib/react-query';
import queryKeys from '../api/queryKeys';
import { useMemo } from 'react';

interface CreativeParams {
  ad_type?: string;
  network?: string;
}

interface UseCreativesReturn {
  rows: CreativeEntry[];
  selectedRows: CreativeEntry[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
}

const createCreativeMap = (
  creatives: CreativeEntry[]
): Map<string, CreativeEntry> => {
  const map = new Map<string, CreativeEntry>();
  creatives.forEach((creative) => {
    if (creative?.id) {
      map.set(creative.id, creative);
    }
  });
  return map;
};

const getCachedCreatives = (): CreativeEntry[] => {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: queryKeys.allCreatives })
    .flatMap((query) => query.state.data as CreativeEntry[])
    .filter(Boolean);
};

const getCombinedCreativesMap = (
  selectedCreativeIds: string[],
  apiRows: CreativeEntry[]
): Map<string, CreativeEntry> => {
  const cachedCreatives = getCachedCreatives();
  const uniqueIds = [...new Set(selectedCreativeIds)];
  const selectedCreatives = cachedCreatives.filter((creative) =>
    uniqueIds.includes(creative.id)
  );
  return createCreativeMap([...selectedCreatives, ...apiRows]);
};

interface UseTikTokCreativesParams {
  params?: CreativeParams;
}

const useTikTokCreatives = ({
  params,
}: UseTikTokCreativesParams = {}): UseCreativesReturn => {
  const { watch } = useFormContext();

  const app = watch('app') as string;
  const adType = watch('adType') as string;
  const network = watch('network') as string;
  const filterCreativeByName = watch('filterCreativeByName') as string[];
  const selectedCreativeIds = watch('selectedCreatives') as string[];

  const {
    data: apiRows = [],
    isLoading,
    isSuccess,
    isError,
    error = {},
  } = useCreatives({
    config: { enabled: !!adType && !!app },
    params: {
      app,
      query: filterCreativeByName ?? '',
      ad_type: params?.ad_type ?? adType ?? 'single_ad',
      network: params?.network ?? network ?? 'Tik Tok',
    },
  });

  const combinedCreativesMap = useMemo(
    () => getCombinedCreativesMap(selectedCreativeIds, apiRows),
    [selectedCreativeIds, apiRows]
  );

  const selectedRows = useMemo(() => {
    if (!selectedCreativeIds?.length) return [];

    const cachedCreatives = getCachedCreatives();
    const uniqueIds = [...new Set(selectedCreativeIds)];
    const selectedMap = createCreativeMap(
      cachedCreatives.filter((creative) => uniqueIds.includes(creative.id))
    );

    return Array.from(selectedMap.values());
  }, [selectedCreativeIds]);

  return {
    rows: Array.from(combinedCreativesMap.values()),
    selectedRows,
    isLoading,
    isSuccess,
    isError,
    error: error ?? {},
  };
};

export default useTikTokCreatives;
